// Compose rotations without Euler-angle accumulation, so repeated turns remain
// continuous through a full revolution and across combined axes.
var MowsilModelRotation = {
  multiply(a, b) {
    const [x, y, z, w] = a, [X, Y, Z, W] = b;
    const q = [w*X+x*W+y*Z-z*Y, w*Y-x*Z+y*W+z*X,
      w*Z+x*Y-y*X+z*W, w*W-x*X-y*Y-z*Z];
    const length = Math.hypot(...q);
    return q.map(value => value / length);
  },
  turn(q, axis, radians) {
    const step = [0, 0, 0, Math.cos(radians / 2)];
    step[axis] = Math.sin(radians / 2);
    return this.multiply(step, q);
  },
  matrix(q) {
    const [x, y, z, w] = q;
    return [1-2*(y*y+z*z), 2*(x*y-z*w), 2*(x*z+y*w),
      2*(x*y+z*w), 1-2*(x*x+z*z), 2*(y*z-x*w),
      2*(x*z-y*w), 2*(y*z+x*w), 1-2*(x*x+y*y)];
  },
  transform(q, point) {
    const m = this.matrix(q), { x, y, z } = point;
    return { x:m[0]*x+m[1]*y+m[2]*z, y:m[3]*x+m[4]*y+m[5]*z,
      z:m[6]*x+m[7]*y+m[8]*z };
  },
  orientation(q) {
    // model-viewer 1.12 expects roll/pitch/yaw, with YXZ Euler ordering.
    const m = this.matrix(q);
    const pitch = Math.asin(-Math.max(-1, Math.min(1, m[5])));
    const regular = Math.abs(m[5]) < 0.9999999;
    const yaw = regular ? Math.atan2(m[2], m[8]) : Math.atan2(-m[6], m[0]);
    const roll = regular ? Math.atan2(m[3], m[4]) : 0;
    return `${roll}rad ${pitch}rad ${yaw}rad`;
  }
};

if (!customElements.get('product-model')) {
  customElements.define('product-model', class ProductModel extends DeferredMedia {
    constructor() {
      super();
    }

    loadContent() {
      super.loadContent();

      Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    }

    setupModelViewerUI(errors) {
      if (errors) return;

      const viewer = this.querySelector('model-viewer');
      if (!this.modelViewerUI) this.modelViewerUI = new Shopify.ModelViewerUI(viewer,
        viewer.dataset.mowsilFullAxis === 'true' ? { focusOnPlay: false } : {});
      if (viewer.dataset.mowsilFullAxis === 'true') this.setupFullAxisRotation(viewer);
    }

    setupFullAxisRotation(viewer) {
      if (viewer.dataset.rotationControlsBound) return;
      viewer.dataset.rotationControlsBound = 'true';
      const initial = {
        orbit: viewer.getAttribute('camera-orbit'),
        fov: viewer.getAttribute('field-of-view'),
        shadow: viewer.getAttribute('shadow-intensity')
      };
      let quaternion = [0, 0, 0, 1];
      let center;
      const controls = document.createElement('div');
      controls.className = 'mowsil-model-rotation';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', 'Full-axis 3D rotation');
      controls.hidden = true;
      // Shopify toggles playback on a tap anywhere outside its own control bar.
      // Keep our toolbar gestures from reaching that handler.
      ['mousedown', 'mouseup', 'touchstart', 'touchend', 'click'].forEach(type => {
        controls.addEventListener(type, event => event.stopPropagation());
      });
      const label = document.createElement('span');
      label.textContent = 'Rotate';
      controls.append(label);
      const announce = document.createElement('span');
      announce.className = 'visually-hidden';
      announce.setAttribute('role', 'status');
      const container = viewer.parentElement;
      const fullscreen = container.querySelector('.shopify-model-viewer-ui__button--fullscreen');
      // Shopify's v1 UI only checks legacy cancellation APIs. Modern browsers
      // expose exitFullscreen instead; keep its existing entry control.
      if (fullscreen) {
        fullscreen.addEventListener('click', event => {
          const active = document.fullscreenElement || document.webkitFullscreenElement;
          const exit = document.exitFullscreen || document.webkitExitFullscreen;
          if (active !== container || !exit) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          const result = exit.call(document);
          if (result && result.catch) result.catch(() => {
            announce.textContent = 'Press Escape to leave fullscreen.';
          });
        }, true);
        ['fullscreenchange', 'webkitfullscreenchange'].forEach(type => {
          container.addEventListener(type, () => {
            const active = document.fullscreenElement || document.webkitFullscreenElement;
            fullscreen.setAttribute('aria-label', active === container ? 'Exit Fullscreen' : 'Enter Fullscreen');
          });
        });
      }
      // Product galleries use native drag/zoom. Retain the fullscreen exit fix
      // above without inserting the optional axis toolbar into the gallery.
      if (viewer.dataset.mowsilRotationToolbar === 'false') return;
      const apply = () => {
        if (!this.modelViewerUI.interacting) this.modelViewerUI.play();
        viewer.orientation = MowsilModelRotation.orientation(quaternion);
        // The source is grounded for AR. Rotate its center with its geometry so
        // the product stays in the middle of the viewer rather than orbiting its base.
        const target = MowsilModelRotation.transform(quaternion, center);
        viewer.cameraTarget = `${target.x}m ${target.y}m ${target.z}m`;
        viewer.shadowIntensity = 0;
        viewer.updateComplete.then(() => viewer.jumpCameraToGoal());
      };
      ['X', 'Y', 'Z'].forEach((axis, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = `${axis} ↻`;
        button.setAttribute('aria-label', `Rotate around ${axis} axis`);
        button.title = `Rotate ${axis} 45°; hold Shift to reverse`;
        button.addEventListener('click', event => {
          quaternion = MowsilModelRotation.turn(quaternion, index, (event.shiftKey ? -1 : 1) * Math.PI / 4);
          apply();
          announce.textContent = `Rotated ${axis} axis. Drag to look around; scroll or pinch to zoom.`;
        });
        controls.append(button);
      });
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.textContent = 'Reset';
      reset.setAttribute('aria-label', 'Reset 3D view');
      reset.addEventListener('click', () => {
        if (!this.modelViewerUI.interacting) this.modelViewerUI.play();
        quaternion = [0, 0, 0, 1];
        viewer.orientation = '0deg 0deg 0deg';
        viewer.cameraOrbit = initial.orbit;
        // Restore the loaded center explicitly. Resolving "auto" while the
        // orientation changes can retain the preceding rotated bounds.
        viewer.cameraTarget = `${center.x}m ${center.y}m ${center.z}m`;
        viewer.fieldOfView = initial.fov;
        viewer.shadowIntensity = Number(initial.shadow);
        viewer.updateComplete.then(() => viewer.jumpCameraToGoal());
        announce.textContent = 'Original 3D view restored.';
      });
      controls.append(reset, announce);
      container.append(controls);
      const ready = () => {
        const target = viewer.getCameraTarget();
        center = { x: target.x, y: target.y, z: target.z };
        controls.hidden = false;
      };
      if (viewer.loaded) ready();
      else viewer.addEventListener('load', ready, { once: true });
    }
  });
}

var MowsilProductAR = {
  cameraPage(button, pageUrl) {
    const modelId = button.getAttribute('data-shopify-model3d-id');
    if (!/^\d+$/.test(modelId || '')) return null;
    const url = new URL(pageUrl);
    // Featured-product blocks on non-product pages keep their Shopify launcher.
    if (!/\/products\/[^/]+\/?$/.test(url.pathname)) return null;
    // A separate document owns the modern WebXR renderer. The gallery keeps
    // Shopify's viewer and cannot pause, resize or replace an active AR scene.
    url.searchParams.set('view', 'mowsil-ar');
    url.searchParams.set('ar_model', modelId);
    url.searchParams.delete('mowsil_ar_fallback');
    url.searchParams.delete('debug-xr');
    return url.href;
  },

  launchAndroid(event, button) {
    const url = this.cameraPage(button, window.location.href);
    if (!url) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.setStatus(button, 'Opening the camera view…');
    window.location.assign(url);
  },

  statusFor(button) {
    const top = Array.from(document.querySelectorAll('[data-mowsil-ar]')).find(
      element => element.getAttribute('data-shopify-model3d-id') === button.getAttribute('data-shopify-model3d-id')
    );
    return top && document.getElementById(top.getAttribute('aria-describedby'));
  },

  setStatus(button, message) {
    const status = this.statusFor(button);
    if (status) {
      status.textContent = message;
      status.hidden = !message;
    }
    return status;
  },

  setup() {
    document.querySelectorAll('[data-mowsil-ar]').forEach(button => {
      if (button.dataset.mowsilArBound) return;
      button.dataset.mowsilArBound = 'true';
      button.addEventListener('click', event => {
        // Leave iOS Quick Look to Shopify's native USDZ handler.
        if (!/Android (?:[7-9]|[1-9]\d)(?:\.|;)/i.test(navigator.userAgent)) return;
        this.launchAndroid(event, button);
      }, true);
    });
    // Modal and featured-product buttons dispatch Shopify's XR event instead
    // of using the top button. Intercept it before Shopify's legacy launcher.
    if (!this.xrListenerBound) {
      document.addEventListener('shopify_xr_launch', event => {
        const model = event.detail;
        if (model?.xrMode !== 'scene_viewer' || !model.element || !model.srcUrl) return;
        this.launchAndroid(event, model.element);
      }, true);
      this.xrListenerBound = true;
    }
    this.showFallback();
  },

  showFallback() {
    const url = new URL(window.location.href);
    const modelId = url.searchParams.get('mowsil_ar_fallback');
    if (!modelId) return;
    const button = Array.from(document.querySelectorAll('[data-mowsil-ar]')).find(
      element => element.getAttribute('data-shopify-model3d-id') === modelId
    );
    if (!button) return;
    const status = document.getElementById(button.getAttribute('aria-describedby'));
    if (status) {
      status.textContent = 'Camera AR could not start. In Chrome on an AR-compatible Android phone, install or update Google Play Services for AR, then try again and allow camera access when asked. You can still explore the 3D model below. ';
      const update = document.createElement('a');
      update.href = 'https://play.google.com/store/apps/details?id=com.google.ar.core';
      update.className = 'link underlined-link';
      update.textContent = 'Install or update AR services';
      const devices = document.createElement('a');
      devices.href = 'https://developers.google.com/ar/devices';
      devices.className = 'link underlined-link';
      devices.textContent = 'Check supported phones';
      status.append(update, ' · ', devices);
      status.hidden = false;
    }
    const gallery = button.closest('.product__media-wrapper')?.querySelector('media-gallery');
    if (gallery) customElements.whenDefined('media-gallery').then(() => {
      const media = Array.from(gallery.querySelectorAll('[data-media-id]')).find(
        element => element.dataset.mediaId.endsWith(`-${modelId}`)
      );
      if (media) gallery.setActiveMedia(media.dataset.mediaId, false);
    });
    url.searchParams.delete('mowsil_ar_fallback');
    window.history.replaceState(window.history.state, '', url.href);
  }
};

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () =>
        this.setupShopifyXR()
      );
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

function initializeProductAR() {
  MowsilProductAR.setup();
  window.ProductModel.loadShopifyXR();
}

// Also initialize when a performance app delays this script until after DOM ready.
if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', initializeProductAR, { once: true });
} else {
  initializeProductAR();
}
